import { FunctionComponent } from 'react'
import { Box, Button } from '@material-ui/core'
import { Check } from 'react-feather'
import { makeStyles } from '@material-ui/styles'
import colors from '../../colors'
import { SPACING } from '../../constants'

export interface PlanCopy {
  tier: string
  name: string
  /** Fallback only - the live amount comes from Stripe via /signup_plans. */
  monthlyPrice: number
  features: string[]
  mostPopular?: boolean
}

// Service fee deliberately lives outside this copy - it's fetched from the API
// so this page can't advertise a rate different from the one actually charged.
export const PLAN_COPY: PlanCopy[] = [
  {
    tier: 'core',
    name: 'Core',
    monthlyPrice: 0,
    features: [
      'Membership tiers & perks',
      'Apple Wallet cards & notifications',
      'Patron web app',
      'Management & reporting',
    ],
  },
  {
    tier: 'pro',
    name: 'Pro',
    monthlyPrice: 109,
    mostPopular: true,
    features: [
      'Everything in Core',
      'SMS campaigns',
      'Advanced dashboards and metrics',
    ],
  },
]

const formatMoney = (amount: number) =>
  `$${Math.round(amount).toLocaleString('en-US')}`

// Whole months read as months ("2 months free"); anything else stays in days
// so a 45-day promo isn't rounded into a promise we don't keep.
const formatTrial = (days: number): string => {
  if (days % 30 === 0) {
    const months = days / 30
    return months === 1 ? '1 month free' : `${months} months free`
  }
  return days === 1 ? '1 day free' : `${days} days free`
}

export interface PlanPricing {
  /** Cents, read live from Stripe. */
  amount: number
  /** 'month', 'year', 'day' ... */
  interval?: string
  /** Set on the Stripe Price. Absent for a plan with no trial. */
  trialPeriodDays?: number | null
}

interface PlanCardsProps {
  value: string
  onChange: (tier: string) => void
  /** tier -> service fee percentage, from the API. */
  serviceFees: Record<string, number>
  /** tier -> live Stripe pricing. Absent tiers fall back to PLAN_COPY. */
  prices: Record<string, PlanPricing>
}

const INTERVAL_LABELS: Record<string, string> = {
  day: '/day',
  week: '/wk',
  month: '/mo',
  year: '/yr',
}

const PlanCards: FunctionComponent<PlanCardsProps> = ({ value, onChange, serviceFees, prices }) => {
  const styles = useStyles()

  // Prefer the live Stripe price so changing STRIPE_PRO_TIER_PRICE_ID updates
  // this page on its own; fall back to the copy if Stripe is unreachable.
  const priceFor = (plan: PlanCopy) => {
    const live = prices[plan.tier]
    if (live) {
      return {
        display: formatMoney(live.amount / 100),
        unit: INTERVAL_LABELS[live.interval ?? 'month'] ?? `/${live.interval}`,
      }
    }
    return { display: formatMoney(plan.monthlyPrice), unit: '/mo' }
  }

  return (
    <Box className={styles.container} role="radiogroup" aria-label="Plan">
      {PLAN_COPY.map((plan) => {
        const selected = value === plan.tier
        const fee = serviceFees[plan.tier]
        const trialDays = prices[plan.tier]?.trialPeriodDays

        return (
          <Box
            key={plan.tier}
            role="radio"
            aria-checked={selected}
            aria-label={plan.name}
            tabIndex={0}
            onClick={() => onChange(plan.tier)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onChange(plan.tier)
              }
            }}
            className={selected ? styles.cardSelected : styles.card}
          >
            {plan.mostPopular && (
              <span className={styles.popularBadge}>MOST POPULAR</span>
            )}

            {/* Clipped by its own corner box rather than overflow:hidden on
                the card, which would cut off the MOST POPULAR badge sitting
                proud of the top edge. */}
            {trialDays ? (
              <div className={styles.ribbonClip}>
                <span className={styles.ribbon}>{formatTrial(trialDays)}</span>
              </div>
            ) : null}

            {/* Plain elements throughout: MUI v5 emotion styles override
                @material-ui/styles v4 classes, so sizes/colors set on a
                Typography here would silently not apply. */}
            <span className={selected ? styles.pillSelected : styles.pill}>
              {plan.name.toUpperCase()}
            </span>

            <div className={styles.priceRow}>
              <span className={styles.price}>{priceFor(plan).display}</span>
              <span className={styles.priceUnit}>{priceFor(plan).unit}</span>
            </div>

            <div className={styles.feeLine}>
              {fee === undefined
                ? ' '
                : <>+ <strong>{fee}%</strong> of membership revenue</>}
            </div>

            <div className={styles.divider} />

            <ul className={styles.featureList}>
              {plan.features.map((feature) => (
                <li key={feature} className={styles.feature}>
                  <span className={styles.checkCircle}>
                    <Check size={12} className={styles.check} />
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              type="button"
              fullWidth
              disableElevation
              variant={selected ? 'contained' : 'outlined'}
              onClick={(e) => {
                e.stopPropagation()
                onChange(plan.tier)
              }}
              // sx (emotion) rather than makeStyles, so these actually beat the
              // theme's contained/uppercase Button defaults.
              sx={{
                mt: 2,
                py: 1.25,
                textTransform: 'none',
                fontSize: 15,
                fontWeight: 700,
                borderRadius: '8px',
                ...(selected
                  ? {}
                  : {
                    color: colors.GREY_1,
                    borderColor: colors.GREY_4,
                    '&:hover': {
                      borderColor: colors.GREY_3,
                      backgroundColor: colors.GREY_7,
                    },
                  }),
              }}
            >
              {selected ? `${plan.name} selected` : `Select ${plan.name}`}
            </Button>
          </Box>
        )
      })}
    </Box>
  )
}

const cardBase = {
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  flex: 1,
  minWidth: 0,
  padding: SPACING * 3,
  borderRadius: 16,
  cursor: 'pointer',
  background: colors.WHITE,
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: SPACING * 2,
    alignItems: 'stretch',
    // Stack rather than squeezing two cards side by side on small screens.
    '@media (max-width: 600px)': {
      flexDirection: 'column',
    },
  },
  card: {
    ...cardBase,
    border: `1px solid ${colors.GREY_4}`,
    '&:hover': { borderColor: colors.GREY_3 },
  },
  cardSelected: {
    ...cardBase,
    border: `2px solid ${colors.BRIGHT_BLUE}`,
    boxShadow: `0 0 0 4px ${colors.PALE_BLUE}`,
  },
  popularBadge: {
    position: 'absolute',
    top: -11,
    left: '50%',
    transform: 'translateX(-50%)',
    background: colors.BRIGHT_BLUE,
    color: colors.WHITE,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    lineHeight: '22px',
    height: 22,
    padding: '0 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  pill: {
    alignSelf: 'flex-start',
    background: colors.GREY_6,
    color: colors.GREY_2,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    lineHeight: '22px',
    height: 22,
    padding: '0 10px',
    borderRadius: 999,
    marginBottom: SPACING * 2,
  },
  pillSelected: {
    alignSelf: 'flex-start',
    background: colors.PALE_BLUE,
    color: colors.BRIGHT_BLUE,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    lineHeight: '22px',
    height: 22,
    padding: '0 10px',
    borderRadius: 999,
    marginBottom: SPACING * 2,
  },
  // A square at the corner that clips the rotated band. Radius matches the
  // card so the ribbon follows the rounded corner instead of squaring it off.
  ribbonClip: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 96,
    height: 96,
    overflow: 'hidden',
    borderTopRightRadius: 16,
    // The card is the click target; the ribbon must not swallow the click.
    pointerEvents: 'none',
  },
  ribbon: {
    position: 'absolute',
    // Geometry note: the band must be centred on the midpoint of the part
    // that is actually VISIBLE, not on its own 160px midpoint - those are
    // different points, and centring on the latter pushes the text up-left
    // off the end of the chord (it ate the leading "2").
    //
    // Band crosses the 96px box 20px in from its left edge, so it cuts both
    // card edges 76px from the corner: a visible run of 76*sqrt(2) ~= 107px,
    // whose midpoint is (58, 38). The band is placed so its centre lands
    // there: left = 58 - 160/2 = -22, hence right = -42; top = 38 - 18/2.
    top: 29,
    right: -42,
    width: 160,
    transform: 'rotate(45deg)',
    background: colors.GREEN,
    color: colors.WHITE,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    lineHeight: '18px',
    textAlign: 'center' as const,
    textTransform: 'uppercase' as const,
    boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
  },
  price: {
    fontSize: 34,
    fontWeight: 700,
    lineHeight: 1.1,
    color: colors.STITCH_BLACK,
  },
  priceUnit: {
    fontSize: 15,
    color: colors.GREY_3,
    marginLeft: 3,
  },
  feeLine: {
    fontSize: 14,
    lineHeight: 1.4,
    color: colors.GREY_1,
    marginTop: SPACING,
  },
  divider: {
    height: 1,
    background: colors.GREY_5,
    margin: `${SPACING * 2.5}px 0`,
  },
  featureList: {
    listStyleType: 'none',
    paddingInlineStart: 0,
    margin: 0,
    // Pushes the CTA to the bottom so both cards' buttons line up.
    flex: 1,
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    lineHeight: 1.4,
    color: colors.GREY_1,
    marginBottom: SPACING * 1.5,
  },
  checkCircle: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: colors.PALE_GREEN,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  check: {
    color: colors.GREEN,
  },
})

export default PlanCards
